<template>
	<div class="details-page" v-loading="loading">
		<DetailsHeroPanel :page-status="pageStatus" :page-meta="pageMeta" @back="handleBack" />

		<BasicInfoSection :basic-info-top="basicInfoTop" :basic-info-bottom="basicInfoBottom" />

		<RealtimeDataSection
			:collector-device-no="detail.collectorDeviceNo"
			:last-update="detail.collectorLastCommunicateTime"
		/>

		<div class="chart-grid">
			<PressureTrendChart :collector-device-no="detail.collectorDeviceNo" />
			<FlowTrendChart :collector-device-no="detail.collectorDeviceNo" />
		</div>

		<div class="data-grid">
			<RelatedDevicesSection :collector-device-no="detail.collectorDeviceNo" />
			<AlarmRecordsSection :regulator-code="detail.regulatorCode" @status-change="handleAlarmStatusChange" />
		</div>
	</div>
</template>

<script>
import DetailsHeroPanel from './components/DetailsHeroPanel.vue'
import BasicInfoSection from './components/BasicInfoSection.vue'
import RealtimeDataSection from './components/RealtimeDataSection.vue'
import PressureTrendChart from './components/PressureTrendChart.vue'
import FlowTrendChart from './components/FlowTrendChart.vue'
import RelatedDevicesSection from './components/RelatedDevicesSection.vue'
import AlarmRecordsSection from './components/AlarmRecordsSection.vue'
import { apiGetPressureRegulatorDetail } from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'VoltageAccountDetails',
	components: {
		DetailsHeroPanel,
		BasicInfoSection,
		RealtimeDataSection,
		PressureTrendChart,
		FlowTrendChart,
		RelatedDevicesSection,
		AlarmRecordsSection,
	},
	data() {
		return {
			loading: false,
			detail: {},
			pageStatus: '',
			pageMeta: '',
			basicInfoTop: [],
			basicInfoBottom: [],
		}
	},
	created() {
		this.getDetailData()
	},
	methods: {
		handleBack() {
			if (this.$router && this.$router.back) {
				this.$router.back()
			}
		},
		async getDetailData() {
			if (!this.regulatorId) {
				this.$message.warning('缺少调压器ID')
				this.handleBack()
				return
			}
			this.loading = true
			try {
				const detail = await apiGetPressureRegulatorDetail(this.regulatorId)
				this.detail = detail || {}
				this.setBasicInfo(this.detail)
				this.setHeroInfo(this.detail)
			} finally {
				this.loading = false
			}
		},
		handleAlarmStatusChange(hasAlarm) {
			this.pageStatus = hasAlarm ? '异常告警' : '运行正常'
		},
		setHeroInfo(detail) {
			this.pageMeta = [
				`调压器编号：${this.formatValue(detail.regulatorCode)}`,
				`安装地址：${this.formatValue(detail.installAddress)}`,
				`调压柜编号：${this.formatValue(detail.cabinetNo)}`,
			].join(' | ')
		},
		setBasicInfo(detail) {
			const paramJson = detail.paramJson || {}
			this.basicInfoTop = [
				{ label: '品牌型号', value: `${this.formatValue(detail.brand)} ${this.formatValue(detail.model)}` },
				{ label: '安装日期', value: this.formatValue(detail.installDate) },
				{ label: '调压柜/箱编号', value: this.formatValue(detail.cabinetNo) },
				{
					label: '关联采集器',
					value: this.formatValue(detail.collectorDeviceNo),
					link: Boolean(detail.collectorDeviceNo),
					deviceId: detail.deviceId,
					deviceTypeId: detail.deviceTypeId,
				},
				{ label: '采集器最近通讯', value: this.formatValue(detail.collectorLastCommunicateTime) },
			]
			this.basicInfoBottom = [
				{ label: '安装地址', value: this.formatValue(detail.installAddress) },
				{ label: '经度', value: this.formatValue(detail.longitude) },
				{ label: '纬度', value: this.formatValue(detail.latitude) },
				{ label: '放散压力', value: this.formatValue(paramJson.reliefPressure) },
				{ label: '切断压力', value: this.formatValue(paramJson.shutdownPressure) },
				{ label: '备注', value: this.formatValue(detail.remark) },
			]
		},
		formatValue(value) {
			return value === null || value === undefined || value === '' ? '--' : value
		},
	},
	computed: {
		regulatorId() {
			return this.$route.query.id
		},
	},
}
</script>

<style lang="scss" scoped>
.details-page {
	--page-bg: #eef4f8;
	--panel-bg: rgba(255, 255, 255, 0.96);
	--panel-border: #dbe5ee;
	--panel-shadow: 0 18px 38px rgba(15, 23, 42, 0.05);
	--title: #0f172a;
	--text: #334155;
	--muted: #64748b;
	--line: #e2e8f0;
	--primary: #2563eb;
	--primary-soft: #eff6ff;
	--success: #16a34a;
	--success-soft: #dcfce7;
	--warning: #ca8a04;
	--warning-soft: #fef9c3;
	--minor: #f97316;
	--minor-soft: #ffedd5;
	--danger: #dc2626;
	--danger-soft: #fee2e2;

	min-height: 100%;
	padding: 12px;
	background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 26%),
		radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 20%),
		linear-gradient(180deg, #f5f9fc 0%, var(--page-bg) 100%);
	display: flex;
	flex-direction: column;
	gap: 12px;
	height: 100%;
	overflow-y: auto;
	font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
	color: var(--text);
}

.details-page > * {
	flex-shrink: 0;
}

.chart-grid,
.data-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 12px;
	flex-shrink: 0;
}

@media (max-width: 1240px) {
	.chart-grid,
	.data-grid {
		grid-template-columns: 1fr;
	}
}
</style>
